"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import type { ProcessedContent } from "./media-utils";
import { buildProcessedContent } from "./media-utils";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
interface UseDropContentResult {
  drop: ApiDrop | null;
  content: ProcessedContent;
  isLoading: boolean;
  error: unknown;
}

/**
 * Custom hook to fetch and process drop content
 */
export const useDropContent = (dropId, dropPartId, maybeDrop) => {
  const $ = _c(38);
  let t0;
  if ($[0] !== dropPartId || $[1] !== maybeDrop) {
    const previewPart = maybeDrop?.parts.find(p => p.part_id === dropPartId);
    t0 = !maybeDrop || !previewPart?.content?.trim() && (previewPart?.media.length ?? 0) === 0;
    $[0] = dropPartId;
    $[1] = maybeDrop;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const shouldFetchDrop = t0;
  let t1;
  if ($[3] !== dropId) {
    t1 = getDropQueryKey(dropId);
    $[3] = dropId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== dropId) {
    t2 = () => fetchDropByIdBatched(dropId);
    $[5] = dropId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== maybeDrop) {
    t3 = previousDrop => previousDrop ?? maybeDrop ?? undefined;
    $[7] = maybeDrop;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = shouldFetchDrop ? undefined : maybeDrop;
  let t5;
  if ($[9] !== shouldFetchDrop || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      placeholderData: t3,
      initialData: t4,
      enabled: shouldFetchDrop,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[9] = shouldFetchDrop;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data: drop,
    isFetching,
    error
  } = useQuery(t5);
  let t6;
  if ($[15] !== drop || $[16] !== dropPartId || $[17] !== error || $[18] !== isFetching) {
    bb0: {
      if (isFetching && !drop) {
        let t7;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = {
            segments: [{
              type: "text",
              content: "Loading..."
            }],
            apiMedia: []
          };
          $[20] = t7;
        } else {
          t7 = $[20];
        }
        t6 = t7;
        break bb0;
      }
      if (error) {
        let t7;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = /Drop [0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12} not found/;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        const regex = t7;
        let t8;
        if ($[22] !== error) {
          const sanitizedError = sanitizeErrorForUser(error);
          t8 = regex.test(sanitizedError) ? "This drop has been deleted by the author" : sanitizedError;
          $[22] = error;
          $[23] = t8;
        } else {
          t8 = $[23];
        }
        const errorMsg = t8;
        let t9;
        if ($[24] !== errorMsg) {
          t9 = [{
            type: "text",
            content: errorMsg
          }];
          $[24] = errorMsg;
          $[25] = t9;
        } else {
          t9 = $[25];
        }
        let t10;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = [];
          $[26] = t10;
        } else {
          t10 = $[26];
        }
        let t11;
        if ($[27] !== t9) {
          t11 = {
            segments: t9,
            apiMedia: t10
          };
          $[27] = t9;
          $[28] = t11;
        } else {
          t11 = $[28];
        }
        t6 = t11;
        break bb0;
      }
      if (!drop) {
        let t7;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = {
            segments: [],
            apiMedia: []
          };
          $[29] = t7;
        } else {
          t7 = $[29];
        }
        t6 = t7;
        break bb0;
      }
      let t7;
      if ($[30] !== dropPartId) {
        t7 = p_0 => p_0.part_id === dropPartId;
        $[30] = dropPartId;
        $[31] = t7;
      } else {
        t7 = $[31];
      }
      const part = drop.parts.find(t7);
      if (!part) {
        let t8;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            segments: [],
            apiMedia: []
          };
          $[32] = t8;
        } else {
          t8 = $[32];
        }
        t6 = t8;
        break bb0;
      }
      t6 = buildProcessedContent(part.content, part.media);
    }
    $[15] = drop;
    $[16] = dropPartId;
    $[17] = error;
    $[18] = isFetching;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const content = t6;
  const t7 = drop ?? null;
  const t8 = isFetching && !drop;
  let t9;
  if ($[33] !== content || $[34] !== error || $[35] !== t7 || $[36] !== t8) {
    t9 = {
      drop: t7,
      content,
      isLoading: t8,
      error
    };
    $[33] = content;
    $[34] = error;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
};